/* eslint-disable no-prototype-builtins */
import { useFavicon } from "@vueuse/core";
import { set } from "lodash-es";
import { useRoute } from "vue-router";

import { useHotelStoreHook } from "@/distributions/consumer/y/store/hotel";
import { useChannelStoreHook } from "@/store/modules/channel";

function supportWebp() {
  // https://stackoverflow.com/questions/5573096/detecting-webp-support
  const elem = document.createElement("canvas");

  if (elem.getContext && elem.getContext("2d")) {
    // was able or not to get WebP representation
    return elem.toDataURL("image/webp").indexOf("data:image/webp") === 0;
  } else {
    // very old browser like IE 8, canvas not supported
    return false;
  }
}
const WEBP_SUPPORTED = supportWebp();
const optimizeUrl = (url, width, scale, optimizeWidth) => {
  url = url.replace(
    "https://qiniu-uploads.charmdeer.com",
    "https://qiniu-uploads-cdn.charmdeer.com"
  );

  let format;
  if (WEBP_SUPPORTED) {
    format = "webp";
  }

  if (optimizeWidth || format) {
    url = `${url}?imageView2/2`;
    if (format) {
      url = `${url}/format/${format}`;
    }
    if (optimizeWidth) {
      url = `${url}/w/${scale}`;
    }
  }
  return url;
};

/**
 * @param  { String, Array } url -- 图片原始地址
 * @param  { Number } width -- 图片在页面中的宽度，单位rem，默认 10
 * @param  { Boolean } optimizeWidth -- 是否优化宽度，默认true
 * @return 修改后的图片地址
 */
export const optimizeQiniuImage = (url, width = 10, optimizeWidth = true) => {
  if (!url) return "";

  const devicePixelRatio = window.devicePixelRatio;
  const screenWidth = window.screen.width;
  const fullWidth = screenWidth * devicePixelRatio;
  const buffer = 1.2 + ((2.4 - 1.2) * (10 - width)) / 9; // 人为增加 20% - 100% 避免图片模糊，简单处理横图在竖框显示的场景
  const scale = Math.round(fullWidth * buffer * (width / 10));

  let newUrl;
  if (typeof url === "string") {
    newUrl = optimizeUrl(url, width, scale, optimizeWidth);
  } else {
    newUrl = url.map(it => {
      return (it && optimizeUrl(it, width, scale, optimizeWidth)) || "";
    });
  }
  return newUrl;
};

/**
 * 设置页面icon
 */
export const setFavicon = () => {
  const { channel } = useChannelStoreHook();
  const icon = useFavicon();
  icon.value = channel?.logoDetail.rectangle || channel?.logo;
};

export const paramsToQuery = () => {
  const YHotelStore = useHotelStoreHook();
  const _searchModel = YHotelStore.searchModel;
  const _hotelSearchModel = YHotelStore.hotelSearchModel;
  if (!_searchModel || !_hotelSearchModel) return {};
  let _query = {};
  _query = {
    locationType: _hotelSearchModel?.locationType,
    showLabel: _hotelSearchModel?.location?.showLabel,
    locationRef: _hotelSearchModel?.locationRef,
    checkIn: _searchModel.stay?.date?.checkIn,
    checkOut: _searchModel.stay?.date?.checkOut,
    numberOfAdults: _searchModel.stay?.guest?.numberOfAdults,
    numberOfChildren: _searchModel.stay?.guest?.numberOfChildren,
    numberOfRooms: _searchModel.stay?.guest?.numberOfRooms
  };
  if (YHotelStore.selectedHotel) {
    _query.hotelId = YHotelStore.selectedHotel._id;
  }
  return _query;
};

export const queryToParams = (queryData = {}) => {
  const route = useRoute();
  const query = queryData || route?.query || {};
  const filter = {};
  const options = {
    extraBrandsRequired: true
  };
  if (query.locationType === "city") {
    filter.city = { _id: query.locationRef };
  } else if (query.locationType === "moduleTpHotel") {
    filter.hotels = [{ _id: query.locationRef }];
  } else if (query.locationType === "googleMapPolitical") {
    set(filter, "_biz.googleMapPolitical", {
      place_id: query.locationRef,
      description: query.showLabel
    });
  } else if (query.locationType === "search") {
    filter.search = query.showLabel;
  }
  return {
    filter,
    location: {
      locationType: query.locationType,
      showLabel: query.showLabel,
      _id: query.locationRef
    },
    stay: {
      date: {
        checkIn: query.checkIn,
        checkOut: query.checkOut
      },
      guest: {
        numberOfAdults: +query.numberOfAdults,
        numberOfChildren: +query.numberOfChildren,
        numberOfRooms: +query.numberOfRooms
      }
    },
    options
  };
};

export const deepEqual = (object1, object2) => {
  if (object1 === object2) {
    return true;
  }
  if (
    object1 == null ||
    typeof object1 != "object" ||
    object2 == null ||
    typeof object2 != "object"
  ) {
    return false;
  }
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);
  if (keys1.length != keys2.length) {
    return false;
  }
  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(object1[key], object2[key])) {
      return false;
    }
  }
  return true;
};

/**
 * 设置meta
 * @param name
 * @param content
 */
export const setMeta = (name, content) => {
  if (!content) return;
  const el = document.querySelector(`meta[name="${name}"]`);
  if (el) {
    el.setAttribute("content", content);
  } else {
    const meta = document.createElement("meta");
    meta.setAttribute("name", name);
    meta.setAttribute("content", content);
    document.head.appendChild(meta);
  }
};

/**
 * 设置页面meta
 * @param seo
 */
export const setPageMeta = seo => {
  if (!seo) return;
  setMeta("Description", seo.description);
  setMeta("keywords", seo.keywords);
};
